import { Box, Button, Text, useToast, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay } from '@chakra-ui/react';
import BrowserCall from '../../pages/my-agent/components/BrowserCall';
import { useState, useRef } from 'react';
import useServerApi from '../../hooks/useServerApi';
import { PATHS } from '../../router/routes';

interface SubscribeBannerProps {
  minutesRemain: number;
  onAddCreditCard: () => void;
  agentId?: string; // Optional agentId prop
}

const SubscribeBanner = ({ minutesRemain, onAddCreditCard, agentId }: SubscribeBannerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const [{ post }] = useServerApi();

  const handleReconfigureClick = () => {
    setIsOpen(true);
  };

  const handleConfirmReconfigure = async () => {
    if (!agentId) {
      toast({
        title: "Error",
        description: "Agent ID is missing",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    try {
      const [success, response] = await post('/rest_endpoints/agents/crud/reconfigure', { agent_id: agentId });
      if (success && response?.status) {
        toast({
          title: "Success",
          description: "Agent reconfiguration started",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        document.location.href = PATHS.COLLECT_COMPANY_DETAILS;
      } else {
        toast({
          title: "Error",
          description: "Failed to reconfigure agent",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error reconfiguring agent:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      setIsOpen(false);
    }
  };

  const onClose = () => setIsOpen(false);

  return (
    <>
      <Box
        mb={4}
        px={6}
        py={3}
        borderRadius="md"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        bgGradient="linear(to-r, #6C63FF, #4F8CFF)"
        color="white"
      >
        <Text fontWeight="bold" fontSize="md">
          Free Mode - {minutesRemain} minutes remaining
        </Text>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            display="none"
            size="sm"
            bg="#4F8CFF"
            color="white"
            fontWeight="bold"
            _hover={{ bg: '#3A66CC' }}
            onClick={handleReconfigureClick}
            isDisabled={!agentId}
          >
            Reconfigure Agent
          </Button>
          {/* Use BrowserCall instead of Talk to agent button */}
          {minutesRemain > 0 && (<BrowserCall id={agentId ? agentId : ""} type="vapi_banner" />)}
          <Button
            bg="#7C3AED"
            color="white"
            fontWeight="bold"
            _hover={{ bg: '#5B21B6' }}
            onClick={onAddCreditCard}
          >
            Add Credit Card
          </Button>
        </Box>
      </Box>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Reconfigure Agent
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to reconfigure this agent? This process may take some time.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleConfirmReconfigure}
                ml={3}
                isLoading={isLoading}
              >
                Confirm
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default SubscribeBanner;
